import { clientSideCookies } from './clientSideCookies';

export const SUBSCRIPTION_OVERRIDE_COOKIE = 'play_subscription_override';
// The unified override header carries { subscription, now, ... }; we send the subscription part.
export const OVERRIDE_HEADER = 'client-platform-override';

export type CustomerType = 'DTC' | 'PARTNER' | 'B2B';

export type SubscriptionOverride = {
  products: number[];
  customerType: CustomerType[];
};

const DEFAULT_CUSTOMER_TYPE: CustomerType[] = ['DTC'];

function isDefaultCustomerType(customerType: CustomerType[]): boolean {
  return customerType.length === 1 && customerType[0] === 'DTC';
}

export function isEmptyOverride(override: SubscriptionOverride): boolean {
  return override.products.length === 0 && isDefaultCustomerType(override.customerType);
}

export function encodeSubscriptionOverride(override: SubscriptionOverride): string {
  return btoa(JSON.stringify(override));
}

// The value of the unified `Client-Platform-Override` header: the subscription wrapped under
// `{ subscription }` so the server can carry other override kinds (e.g. now) in the same header.
export function encodeOverrideHeader(override: SubscriptionOverride): string {
  return btoa(JSON.stringify({ subscription: override }));
}

export function decodeSubscriptionOverride(encoded: string): SubscriptionOverride | null {
  try {
    const parsed = JSON.parse(atob(encoded)) as Partial<SubscriptionOverride>;
    const products = Array.isArray(parsed.products)
      ? parsed.products.filter((id): id is number => typeof id === 'number')
      : [];
    const customerType =
      Array.isArray(parsed.customerType) && parsed.customerType.length > 0
        ? (parsed.customerType as CustomerType[])
        : DEFAULT_CUSTOMER_TYPE;
    return { products, customerType };
  } catch {
    return null;
  }
}

export function readSubscriptionOverride(): SubscriptionOverride {
  const cookie = clientSideCookies.getCookie(SUBSCRIPTION_OVERRIDE_COOKIE);
  const decoded = cookie ? decodeSubscriptionOverride(decodeURIComponent(cookie)) : null;
  return decoded ?? { products: [], customerType: DEFAULT_CUSTOMER_TYPE };
}

export function writeSubscriptionOverride(override: SubscriptionOverride): void {
  if (isEmptyOverride(override)) {
    clearSubscriptionOverride();
    return;
  }

  clientSideCookies.setCookie({
    key: SUBSCRIPTION_OVERRIDE_COOKIE,
    value: encodeSubscriptionOverride(override),
    maxAge: 60 * 60 * 24 * 30,
    secure: false,
  });
}

export function clearSubscriptionOverride(): void {
  clientSideCookies.setCookie({
    key: SUBSCRIPTION_OVERRIDE_COOKIE,
    value: '',
    maxAge: 0,
    secure: false,
  });
}

/**
 * Header value for the unified `Client-Platform-Override` header attached to pages-graph requests.
 * Wraps the subscription override under `{ subscription }`. Returns null when no override is set.
 */
export function getSubscriptionOverrideHeaderValue(): string | null {
  if (typeof window === 'undefined') return null;

  const cookie = clientSideCookies.getCookie(SUBSCRIPTION_OVERRIDE_COOKIE);
  if (!cookie) return null;

  const override = decodeSubscriptionOverride(decodeURIComponent(cookie));
  if (!override || isEmptyOverride(override)) return null;

  return encodeOverrideHeader(override);
}
